// https://github.com/JohnCampionJr/vite-plugin-vue-layouts/issues/114#issuecomment-1665922024
import { setupLayouts } from 'virtual:generated-layouts'
import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'
import i18n from '@/plugins/i18n'
import routes from '~pages'
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'

// Declare types for a route meta
declare module 'vue-router' {
  interface RouteMeta {
    title?: string
    requiresAuth: boolean
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    ...setupLayouts(routes),
  ],
  scrollBehavior() {
    // always scroll to top
    return { top: 0 }
  },
})

const updateDocumentTitle = (to: RouteLocationNormalized) => {
  const appStore = useAppStore()
  let title = (to.meta.title) ?? 'page_titles.myfj'
  title = i18n.global.t(title)
  document.title = `${appStore.config.APP_NAME} - ${title}`
}

const loadUserData = async (): Promise<boolean> => {
  const authStore = useAuthStore()

  try {
    await authStore.getLoggedInUser()

    return authStore.authorised
  }
  catch (error) {
    // Check if this was a shadow student mismatch after token refresh
    if (error instanceof Error && error.message === 'SHADOW_STUDENT_MISMATCH' && authStore.hasValidAccessToken) {
      // Token was refreshed but student changed (multi-tab shadowing)
      // Try loading user data again with fresh state
      try {
        await authStore.getLoggedInUser()

        return authStore.authorised
      }
      catch (retryError) {
        // Second attempt failed - fall through to normal error handling
      }
    }

    // If call failed, check if we still have refresh token
    // If refresh token is gone, auth failed
    if (!authStore.hasRefreshToken) {
      return false
    }

    // Still have refresh token - might have been a transient error
    // Check if user became authorized anyway
    return authStore.authorised
  }
}

const handleAuth = async (to: RouteLocationNormalized) => {
  const authStore = useAuthStore()

  // If we have a valid token and user is already loaded, we're good
  if (authStore.hasValidAccessToken && authStore.authorised) {
    return null
  }

  // If we have tokens (either valid access token or refresh token), try to load user data
  if (!authStore.hasRefreshToken) {
    // No tokens at all - need to login
    window.sessionStorage.setItem('requested_page', to.fullPath)
    return { name: 'auth.login' }
  }

  const isAuthorised = await loadUserData()

  if (isAuthorised) {
    return null
  }

  // User data loaded but not authorized
  return { name: 'auth.unauthorised' }
}

//@ts-ignore
router.beforeEach(async to => {
  const authStore = useAuthStore()

  updateDocumentTitle(to)

  const routerRecord = to.matched.find(record => record.meta.requiresAuth)
  if (!routerRecord) return // No need to check further if the route doesn't require auth

  // Handle authentication - check for valid token or refresh token
  // If we have a refresh token but no valid access token, handleAuth will trigger
  // an API call that the axios interceptor will catch and refresh automatically
  if (!authStore.hasValidAccessToken || !authStore.authorised) {
    const result = await handleAuth(to)

    if (result) return result // If handleAuth returns a result, use it
  }

  // Check if the user is authorized
  if (!authStore.authorised) {
    return { name: 'auth.unauthorised' }
  }
})

export default router
